/*
TODO: Mixpanel events
*/
import React from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { InstallmentPlan } from 'apollo/queries/autogenerate/schemas';
import { AMZ_SUB_MERCHANTS_IDS } from 'constants/amazon';
import { COMBINI_FEE } from 'shared/constants/fees';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import generateID from 'shared/modules/common/utils/generate-id';
import {
  convertInstallmentKindToNumber,
  toFullWidth,
} from 'shared/modules/usage/utils/charge-utils';
import { formatYearMonthDate, formatToCurrency } from 'shared/utils/japanese';
import testID from 'shared/utils/test-id';
import { convertAmazonPayTitle } from 'utils/amazon-pay-default-storename';
import InstallmentOverview from 'web/modules/common/components/molecules/InstallmentOverview';
// import Mixpanel from 'vendor/Mixpanel';

type Props = {
  installmentPlan: InstallmentPlan;
  currentInstallmentIndex: number;
  onViewReceiptPress: () => void;
};

// const screen = 'Receipt Dialog';
export default function InstallmentPlanView(props: Props) {
  if (!props.installmentPlan) {
    return null;
  }

  const payment = props.installmentPlan.payment;

  const iconSource = require('shared/assets/images/icons/charge-kind/capture@3x.png');

  const iconTitle = `${toFullWidth(
    convertInstallmentKindToNumber(props.installmentPlan.kind)
  )}回あと払い支払額`;

  const isAmazonPay =
    payment?.subMerchantId === AMZ_SUB_MERCHANTS_IDS.amazonPay;

  // If the storeName of Amazon Pay purchase starts with 'AMZ', should display default title
  const shouldConvertAmazonPayDefaultTitle =
    !!payment?.storeName && convertAmazonPayTitle(payment?.storeName);

  // If the the payment is Amazon Pay purchase, should display following title
  const amazonPayTitle = isAmazonPay
    ? payment?.storeName === null ||
      payment?.storeName === '<NO NAME>' ||
      payment?.storeName === undefined ||
      shouldConvertAmazonPayDefaultTitle
      ? 'Amazon Pay提携サイト'
      : payment?.storeName
    : null;

  // If the payment is Amazon Purchase besides Amazon Pay and the storeName is null, should display the default value 'Amazon.co.jp'
  const isAmazon =
    payment?.subMerchantId === AMZ_SUB_MERCHANTS_IDS.amazonPrime ||
    AMZ_SUB_MERCHANTS_IDS.amazonKindle ||
    AMZ_SUB_MERCHANTS_IDS.amazonMusic ||
    AMZ_SUB_MERCHANTS_IDS.amazonPharmacy ||
    AMZ_SUB_MERCHANTS_IDS.amazonPrimeVideo ||
    AMZ_SUB_MERCHANTS_IDS.amazonRetail ||
    AMZ_SUB_MERCHANTS_IDS.amazonMarketplace;

  const amazonTitle = isAmazon
    ? payment?.storeName === null || payment?.storeName === '<NO NAME>'
      ? 'Amazon.co.jp'
      : payment?.storeName
    : null;
  const title = amazonPayTitle ?? amazonTitle ?? payment?.storeName;
  const subtitle = payment?.byWayOf;
  const createdAt = payment?.createdAt;
  const maxInstallmentAmount =
    Math.max(
      ...props.installmentPlan.installments.map(
        (installment) => installment.amount
      )
    ) || 0;

  return (
    <ScrollView style={styles.container}>
      <View style={styles.headerContainer}>
        <View style={{ flex: 1 }}>
          <View style={styles.iconContainer}>
            <Image source={iconSource} style={styles.iconImage} />
            <Text style={styles.iconTitle}>{iconTitle}</Text>
          </View>
          <Text style={styles.headerTitle}>{title}</Text>
          {subtitle && <Text style={styles.headerSubtitle}>{subtitle}</Text>}
          {/* For Amazon Pay payments, display division title */}
          {!!isAmazonPay && (
            <Text style={styles.headerDivisiontitle}>Amazon Pay</Text>
          )}
          <Text style={styles.transactionDate}>
            {!!payment && '購入日 '}
            {!!createdAt && formatYearMonthDate(createdAt, 'en')}
          </Text>
        </View>
      </View>
      <View style={styles.separator} />
      <View style={[styles.infoRow, { marginBottom: 16 }]}>
        <Text style={styles.infoLabel}>合計</Text>
        <Text style={styles.infoValue}>
          {formatToCurrency(props.installmentPlan.amount)}
        </Text>
      </View>
      <View style={styles.infoRow}>
        <Text style={styles.infoLabel}>お支払い回数</Text>
        <Text style={styles.infoValue}>
          {' '}
          {formatToCurrency(maxInstallmentAmount)} x{' '}
          {props.installmentPlan.installments.length}
          {'回'}
        </Text>
      </View>
      <View style={styles.separator} />
      <InstallmentOverview
        currentInstallmentIndex={props.currentInstallmentIndex}
        selectedOption={props.installmentPlan}
      />
      <View style={styles.separator} />

      <View style={styles.body}>
        <TouchableOpacity
          style={styles.button}
          onPress={props.onViewReceiptPress}
          {...testID('show_receipt_button')}
          /** Generate HTML and Test IDs  */
          {...generateID('installment-plan-view-show-receipt-button')}
          /** Platform-specific props */
          {...Platform.select({
            web: {
              accessibilityRole: 'button',
            },
          })}
        >
          <Text style={styles.buttonText}>ご利用明細</Text>
        </TouchableOpacity>
      </View>
      <View>
        <Text style={styles.disclaimerText}>
          ※ 端数がある場合は各回のお支払い金額が異なります。
        </Text>
        <Text style={styles.disclaimerText}>
          {`※ コンビニ払いの場合、支払手数料が別途発生します（最大税込${COMBINI_FEE}円/回）。`}
        </Text>
        <Text style={styles.disclaimerText}>
          ※
          口座振替は毎月12日（金融機関休業日の場合は翌営業日）に引き落としになります。1月および5月請求分の引き落とし日は20日（金融機関休業日の場合は翌営業日）になる場合があります。
        </Text>
        <Text style={styles.disclaimerText}>
          ※
          ご利用明細は、お支払い履歴を示すものではありませんので、ご注意ください。
        </Text>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: colors.white,
    padding: 24,
  },
  headerContainer: {
    flexDirection: 'row',
    backgroundColor: colors.white,
    marginBottom: 8,
  },
  headerTitle: {
    ...typography.title03,
    fontSize: 16,
    paddingTop: 8,
    paddingBottom: 4,
  },
  headerSubtitle: {
    ...typography.caption02,
    paddingBottom: 8,
  },
  headerDivisiontitle: {
    ...typography.caption02,
    paddingTop: 12,
    paddingBottom: 16,
  },
  transactionDate: {
    ...typography.caption02,
    color: colors.greyDark,
  },
  body: {
    backgroundColor: colors.white,
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  separator: {
    width: '100%',
    height: 0.5,
    backgroundColor: colors.greyLight,
    marginBottom: 16,
    marginTop: 19,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  infoLabel: {
    ...typography.caption02,
  },
  infoValue: {
    ...typography.caption01,
  },
  button: {
    marginTop: 19,
    borderWidth: 2,
    borderColor: colors.greyDark,
    borderRadius: 32,
    height: 56,
    width: 183,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  buttonText: {
    fontSize: 16,
  },

  // Icon
  iconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconTitle: {
    ...typography.title03,
    marginLeft: 8,
    color: colors.teal,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 'bold',
  },
  iconImage: {
    width: 24,
    height: 24,
  },
  disclaimerText: {
    ...typography.caption02,
    marginTop: 16,
    color: colors.grey,
  },
});
